import {Separator} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {isNotEmpty, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {type Nullish, type RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useDailyStockTakeFormatters} from '../hooks/useDailyStockTakeFormatters';
import type {DailyStockTakeItem, DailyStockTakeMoney} from '../utils/buildDailyStockTakeDetailView';
import {formatDailyStockTakeItem} from '../utils/formatDailyStockTakeItem';

interface DailyStockTakeBreakdownProps extends RequiredTestIdProps {
  items: DailyStockTakeItem[];
  totalLabel: string;
  total: DailyStockTakeMoney | null;
  /** Documents behind the total, reported per warehouse and day by the closing job. */
  totalDocuments?: number | Nullish;
  /** Cards behind the total. The closing job reports it per day, not per line. */
  totalArticles?: number | Nullish;
}

/** One breakdown: its lines, then the total below a rule — the shape both cards share. */
export function DailyStockTakeBreakdown(props: DailyStockTakeBreakdownProps) {
  const {money} = useDailyStockTakeFormatters();

  return (
    <VStack spacing={2}>
      {props.items.map((item) => (
        <HStack key={item.id} spacing={3} justify="space-between" align="flex-start">
          <Text size="xSmall" data-testid={suffixTestId(`${item.id}-label`, props)}>
            {formatDailyStockTakeItem(item).label}
          </Text>
          <VStack spacing={0} align="flex-end">
            <Text size="xSmall" alternative data-testid={suffixTestId(`${item.id}-amount`, props)}>
              {money(item.amount)}
            </Text>
            {/*
              The counts are secondary to the amount, so they share one line under it rather
              than taking columns of their own.
            */}
            <Show when={isNotEmpty(formatDailyStockTakeItem(item).note)}>
              <Text
                size="xxSmall"
                color="secondary"
                noWrap
                data-testid={suffixTestId(`${item.id}-counts`, props)}
              >
                {formatDailyStockTakeItem(item).note}
              </Text>
            </Show>
          </VStack>
        </HStack>
      ))}

      {/* A rule with nothing above it would promise lines the closing job does not report. */}
      <Show when={isNotEmpty(props.items)}>
        <Separator spacing={0} />
      </Show>

      <HStack spacing={3} justify="space-between" align="flex-start">
        <Text size="xSmall" alternative data-testid={suffixTestId('total-label', props)}>
          {props.totalLabel}
        </Text>
        <VStack spacing={0} align="flex-end">
          <Text size="xSmall" alternative data-testid={suffixTestId('total-amount', props)}>
            {money(props.total)}
          </Text>
          <Show when={isNotNil(props.totalDocuments)}>
            <Text
              size="xxSmall"
              color="secondary"
              noWrap
              data-testid={suffixTestId('total-documents', props)}
            >
              {i18n.t('entity.warehouse.reporting.documentsLine', {
                count: props.totalDocuments ?? 0,
              })}
            </Text>
          </Show>
          <Show when={isNotNil(props.totalArticles)}>
            <Text
              size="xxSmall"
              color="secondary"
              noWrap
              data-testid={suffixTestId('total-articles', props)}
            >
              {/* A number for the same reason as the document count above. */}
              {i18n.t('entity.warehouse.reporting.articlesLine', {
                count: props.totalArticles ?? 0,
              })}
            </Text>
          </Show>
        </VStack>
      </HStack>
    </VStack>
  );
}
